import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Search, Package, MapPin, SlidersHorizontal, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { inventoryApi } from "./inventoryApi";
import { PART_STATUS, VALUE_TYPE_BADGE, formatCurrency } from "./inventoryConstants";
import { PartFormModal } from "./PartFormModal";
import { PartDetailModal } from "./PartDetailModal";
import { BulkImportModal } from "./BulkImportModal";

const StatusBadge = ({ status }) => {
  const s = PART_STATUS[status] || PART_STATUS.in_stock;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${s.bg} ${s.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} /> {s.label}
    </span>
  );
};

const PartCard = ({ part, onClick }) => {
  const vt = VALUE_TYPE_BADGE[part.value_type] || VALUE_TYPE_BADGE.estimated;
  return (
    <button
      onClick={() => onClick(part)}
      data-testid={`part-card-${part.id}`}
      className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md hover:border-blue-200 transition-all"
    >
      <div className="flex items-start gap-3">
        {part.photo ? (
          <img src={part.photo} alt={part.name} className="w-12 h-12 rounded-lg object-cover border flex-shrink-0" />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Package className="w-6 h-6 text-slate-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-slate-900 truncate">{part.name}</span>
            <StatusBadge status={part.status} />
          </div>
          <div className="text-xs text-slate-500 mt-0.5 font-mono">{part.part_number}</div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-slate-600">
            <span className="font-medium">On hand: <span className="text-slate-900">{part.quantity_on_hand}</span></span>
            <span>Avail: {part.quantity_available}</span>
            {part.location_label && (
              <span className="inline-flex items-center gap-1 text-slate-500"><MapPin className="w-3 h-3" />{part.location_label}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-sm font-semibold text-slate-900">{formatCurrency(part.inventory_value)}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${vt.bg} ${vt.color}`}>{vt.label}</span>
            {part.category && <span className="text-[10px] text-slate-500">{part.category}</span>}
          </div>
        </div>
      </div>
    </button>
  );
};

export const PartsInventory = ({ currentUser, canManage, vehicles = [] }) => {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [lowStock, setLowStock] = useState(false);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editPart, setEditPart] = useState(null);
  const [detailPart, setDetailPart] = useState(null);

  const fetchParts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (category) params.category = category;
      if (status) params.status = status;
      if (lowStock) params.low_stock = true;
      setParts(await inventoryApi.getParts(params));
    } catch {
      toast.error("Failed to load parts");
    }
    setLoading(false);
  }, [search, category, status, lowStock]);

  useEffect(() => {
    inventoryApi.getCategories().then(setCategories).catch(() => {});
    inventoryApi.getLocations().then(setLocations).catch(() => {});
    inventoryApi.getVendors().then(setVendors).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchParts, 250);
    return () => clearTimeout(t);
  }, [fetchParts]);

  // Keep an open detail view in sync after stock moves / edits
  useEffect(() => {
    if (!detailPart) return;
    const fresh = parts.find((p) => p.id === detailPart.id);
    if (fresh) setDetailPart(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts]);

  const openAdd = () => { setEditPart(null); setShowForm(true); };
  const openEdit = (part) => { setDetailPart(null); setEditPart(part); setShowForm(true); };

  return (
    <div className="space-y-4" data-testid="parts-inventory">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-9" placeholder="Search parts, numbers, manufacturer..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="parts-search" />
        </div>
        {canManage && (
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-parts-btn"><Upload className="w-4 h-4 mr-1" /> Import</Button>
            <Button onClick={openAdd} data-testid="add-part-btn"><Plus className="w-4 h-4 mr-1" /> Add Part</Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="filter-category">
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status">
          <option value="">All Status</option>
          {Object.entries(PART_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={() => setLowStock((v) => !v)} className={`h-9 px-3 rounded-md border text-sm inline-flex items-center gap-1 ${lowStock ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-white border-slate-200 text-slate-600"}`} data-testid="filter-lowstock">
          <SlidersHorizontal className="w-3.5 h-3.5" /> Low Stock
        </button>
        <span className="text-sm text-slate-500 ml-auto">{parts.length} parts</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : parts.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>No parts found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {parts.map((p) => <PartCard key={p.id} part={p} onClick={setDetailPart} />)}
        </div>
      )}

      <PartFormModal
        open={showForm}
        onClose={() => setShowForm(false)}
        part={editPart}
        locations={locations}
        categories={categories}
        vendors={vendors}
        currentUser={currentUser}
        onSaved={fetchParts}
      />
      <PartDetailModal
        part={detailPart}
        onClose={() => setDetailPart(null)}
        onEdit={openEdit}
        onChanged={fetchParts}
        canManage={canManage}
        currentUser={currentUser}
        vehicles={vehicles}
        locations={locations}
      />
      <BulkImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        currentUser={currentUser}
        onImported={fetchParts}
      />
    </div>
  );
};
